import { useEffect, useState, type FormEvent } from 'react'
import { CheckCircle2, Copy, Loader2, RefreshCw, ShieldCheck } from 'lucide-react'
import * as api from '../api/auth'
import { ApiError } from '../api/auth'
import { TwoFactorQr } from './TwoFactorQr'
import { useIsTouch } from '../hooks/useIsTouch'
import type { TwoFactorSetup } from '../types/identity'

/**
 * Manager-2FA policy (D-21): assistant manager and up — and every platform
 * admin — must pair an authenticator app before using account features.
 */
export function TwoFactorEnrolment({ onDone, onSignOut }: { onDone: () => void; onSignOut?: () => void }) {
  const touch = useIsTouch()
  const [setup, setSetup] = useState<TwoFactorSetup | null>(null)
  const [codes, setCodes] = useState<string[]>([])
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [starting, setStarting] = useState(true)
  const [attempt, setAttempt] = useState(0)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setStarting(true)
    setError(null)
    void (async () => {
      try {
        const result = await api.startTwoFactorSetup()
        if (!cancelled) setSetup(result)
      } catch (caught) {
        if (!cancelled) {
          setError(caught instanceof ApiError ? caught.message : 'Could not start two-factor setup')
        }
      } finally {
        if (!cancelled) setStarting(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [attempt])

  const retrySetup = () => setAttempt((current) => current + 1)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!setup) return
    setBusy(true)
    setError(null)
    void (async () => {
      try {
        const result = await api.confirmTwoFactor(code)
        setCodes(result.recovery_codes)
      } catch (caught) {
        setError(caught instanceof ApiError ? caught.message : 'That code did not work')
      } finally {
        setBusy(false)
      }
    })()
  }

  return (
    <form onSubmit={submit} className="card w-full max-w-md space-y-4 p-5">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-salsa-500" aria-hidden="true" />
        <h1 className="text-sm font-semibold text-slate-900 dark:text-white">
          {codes.length > 0 ? 'Save your recovery codes' : 'Two-factor authentication is required'}
        </h1>
      </div>

      {error && setup && (
        <p className="rounded-xl border border-salsa-200 bg-salsa-50/70 px-3 py-2 text-xs text-salsa-800 dark:border-salsa-500/25 dark:bg-salsa-500/10 dark:text-salsa-200">
          {error}
        </p>
      )}

      {codes.length > 0 ? (
        <>
          <p className="text-xs text-slate-600 dark:text-slate-400">
            Each recovery code works once — store them somewhere safe before continuing.
          </p>
          <ul className="grid grid-cols-2 gap-1 font-mono text-xs">
            {codes.map((item) => (
              <li
                key={item}
                className="rounded-lg bg-slate-100 px-2 py-1 text-center text-slate-700 dark:bg-white/5 dark:text-slate-200"
              >
                {item}
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-salsa-600 hover:bg-salsa-500/10 dark:text-salsa-400"
              onClick={() => void navigator.clipboard.writeText(codes.join('\n'))}
            >
              <Copy className="h-3 w-3" aria-hidden="true" /> Copy codes
            </button>
            <button
              type="button"
              onClick={onDone}
              className="ml-auto inline-flex items-center gap-2 rounded-xl bg-salsa-500 px-4 py-2 text-sm font-semibold text-white hover:bg-salsa-600"
            >
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> Continue to Store OS
            </button>
          </div>
        </>
      ) : !setup ? (
        starting ? (
          <p className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            Requesting a fresh secret…
          </p>
        ) : (
          <div className="space-y-2 rounded-xl border border-salsa-200 bg-salsa-50/70 px-3 py-2 text-xs text-salsa-800 dark:border-salsa-500/25 dark:bg-salsa-500/10 dark:text-salsa-200">
            <p>{error ?? 'Could not start two-factor setup'}</p>
            <button
              type="button"
              onClick={retrySetup}
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 font-semibold text-salsa-600 hover:bg-salsa-500/10 dark:text-salsa-400"
            >
              <RefreshCw className="h-3 w-3" aria-hidden="true" /> Try again
            </button>
          </div>
        )
      ) : (
        <>
          <TwoFactorQr otpauthUri={setup.otpauth_uri} secret={setup.secret} account={setup.account} />
          <input
            value={code}
            onChange={(event) => setCode(event.target.value)}
            inputMode="numeric"
            // On a phone the keyboard would open over the QR and the setup key
            // before anything is paired, so only autofocus on a desk (D-108).
            autoFocus={!touch}
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="123456"
            aria-label="6-digit code"
            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-center font-mono text-lg tracking-[0.3em] dark:border-white/10 dark:bg-white/5"
            required
          />
          <button
            type="submit"
            disabled={busy}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-salsa-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-salsa-600 disabled:opacity-60"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Confirm and continue
          </button>
        </>
      )}

      {onSignOut && (
        <button
          type="button"
          onClick={onSignOut}
          className="w-full text-center text-xs font-medium text-slate-500 hover:text-salsa-600 dark:text-slate-400"
        >
          Sign out instead
        </button>
      )}
    </form>
  )
}
